export class Accordion {
  constructor() {
    this._openHeight = 0;
    this._windowWidth = window.innerWidth;
    this._contentObservers = new WeakMap();
    this._documentClickHandler = this._documentClickHandler.bind(this);
    this._windowResizeHandler = this._windowResizeHandler.bind(this);
  }

  init() {
    this.fullUpdate();
    document.addEventListener('click', this._documentClickHandler);
    window.addEventListener('resize', this._windowResizeHandler);
  }

  _documentClickHandler(evt) {
    const target = evt.target;
    if (!target.closest('[data-accordion="button"]')) {
      return;
    }

    evt.preventDefault();
    const parent = target.closest('[data-accordion="parent"]');

    if (parent.dataset.destroy && !window.matchMedia(parent.dataset.destroy).matches) {
      return;
    }

    const element = target.closest('[data-accordion="element"]');
    if (element.classList.contains('is-active')) {
      this.closeAccordion(element);
      return;
    }
    this.openAccordion(element);
  }

  _windowResizeHandler() {
    if (this._windowWidth === window.innerWidth) {
      return;
    }
    this._windowWidth = window.innerWidth;
    this.updateAccordionsHeight();
  }

  _getContent(element) {
    return element.querySelector(':scope > [data-accordion="content"]')
      || element.querySelector('[data-accordion="content"]');
  }

  _isDestroyed(parent) {
    return Boolean(parent?.dataset.destroy && !window.matchMedia(parent.dataset.destroy).matches);
  }

  _syncContentHeight(element, {animate = false, silent = false} = {}) {
    const content = this._getContent(element);
    if (!content) {
      return;
    }

    const parent = element.closest('[data-accordion="parent"]');
    if (this._isDestroyed(parent)) {
      content.style.maxHeight = '100%';
      return;
    }

    if (!element.classList.contains('is-active')) {
      return;
    }

    const nextHeight = `${content.scrollHeight}px`;

    if (silent) {
      content.style.maxHeight = nextHeight;
      return;
    }

    if (!animate) {
      content.style.transition = 'none';
    }

    content.style.maxHeight = nextHeight;

    if (!animate) {
      void content.offsetHeight;
      setTimeout(() => {
        content.style.transition = null;
      });
    }
  }

  _observeContent(element) {
    const content = this._getContent(element);
    if (!content || this._contentObservers.has(content)) {
      return;
    }

    const observer = new ResizeObserver(() => {
      if (!element.classList.contains('is-active')) {
        return;
      }

      // Keep max-height in sync without accordion transition,
      // so inner animations (moreless, etc.) drive the growth.
      this._syncContentHeight(element, {silent: true});

      const nestedParent = element.closest('[data-accordion="parent"]');
      const outerElement = nestedParent?.closest('[data-accordion="element"]');
      if (outerElement?.classList.contains('is-active')) {
        this._syncContentHeight(outerElement, {silent: true});
      }
    });

    Array.from(content.children).forEach((child) => {
      observer.observe(child);
    });

    this._contentObservers.set(content, observer);
  }

  _unobserveContent(element) {
    const content = this._getContent(element);
    if (!content) {
      return;
    }

    const observer = this._contentObservers.get(content);
    if (!observer) {
      return;
    }

    observer.disconnect();
    this._contentObservers.delete(content);
  }

  closeAllAccordion(parent) {
    const elements = parent.querySelectorAll('[data-accordion="element"]');
    elements.forEach((element) => {
      const currentParent = element.closest('[data-accordion="parent"]');
      if (currentParent === parent) {
        this.closeAccordion(element);
      }
    });
  }

  updateAccordionsHeight(element = null) {
    if (element) {
      this._syncContentHeight(element);
      return;
    }

    const closeElements = document.querySelectorAll('[data-accordion="element"]:not(.is-active)');

    closeElements.forEach((closeElement) => {
      const parent = closeElement.closest('[data-accordion="parent"]');
      const content = this._getContent(closeElement);
      if (!content) {
        return;
      }
      if (this._isDestroyed(parent)) {
        content.style.maxHeight = '100%';
        return;
      }
      content.style.maxHeight = null;
    });

    const openElements = document.querySelectorAll('[data-accordion="element"].is-active');
    openElements.forEach((openElement) => {
      this._observeContent(openElement);
      this._syncContentHeight(openElement);
    });
  }

  fullUpdate(parent = null, transition = false) {
    let openElements;
    if (parent) {
      openElements = parent.querySelectorAll('[data-accordion="element"].is-active');
    } else {
      openElements = document.querySelectorAll('[data-accordion="element"].is-active');
    }
    openElements.forEach((openElement) => {
      const innerParent = openElement.querySelector('[data-accordion="parent"]');
      if (innerParent) {
        return;
      }
      this.openAccordion(openElement, transition);
    });
    this.updateAccordionsHeight();
  }

  openAccordion(element, transition = true) {
    const parentElement = element.closest('[data-accordion="parent"]');
    const contentElement = this._getContent(element);
    this._openHeight += contentElement.scrollHeight;

    if (parentElement.hasAttribute('data-single')) {
      this.closeAllAccordion(parentElement);
    }

    element.classList.add('is-active');
    this._observeContent(element);

    if (transition) {
      contentElement.style.maxHeight = `${this._openHeight}px`;
    } else {
      contentElement.style.transition = 'none';
      contentElement.style.maxHeight = `${this._openHeight}px`;
      setTimeout(() => {
        contentElement.style.transition = null;
      });
    }

    if (parentElement.closest('[data-accordion="element"]')) {
      this.openAccordion(parentElement.closest('[data-accordion="element"]'), transition);
      return;
    }

    this._openHeight = 0;
  }

  closeAccordion(element, transition = true) {
    const contentElement = this._getContent(element);
    if (!contentElement) {
      return;
    }
    this._unobserveContent(element);
    element.classList.remove('is-active');
    if (transition) {
      contentElement.style.maxHeight = '0';
    } else {
      contentElement.style.transition = 'none';
      contentElement.style.maxHeight = '0';
      setTimeout(() => {
        contentElement.style.transition = null;
      });
    }
  }
}
